import { CopyIcon, InfoFilledIcon, LinkIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  IconButton,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import React from 'react'
import {
  useIcsCalendarLinkControllerCreateIcsCalendarLink,
  useIcsCalendarLinkControllerGetIcsCalendarLink
} from '~/clinician-api'

const SyncBlueprintCalendar = () => {
  const toast = useToast()
  const { data: calendarLinkData, refetch: refetchCalendarLink } =
    useIcsCalendarLinkControllerGetIcsCalendarLink()

  const { mutateAsync: createCalendarLink, isLoading: isCreating } =
    useIcsCalendarLinkControllerCreateIcsCalendarLink({
      mutation: {
        onSuccess: () => {
          toast({
            title: 'Calendar published',
            description:
              'Your Blueprint calendar is now available for external use',
            status: 'success',
            duration: 5000,
            isClosable: true
          })
          refetchCalendarLink()
        },
        onError: () => {
          toast({
            title: 'Failed to publish calendar',
            description: 'There was an error creating your calendar link',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
      }
    })

  const handlePublishCalendar = async () => {
    await createCalendarLink()
  }

  const handleCopyLink = () => {
    if (calendarLinkData?.url) {
      navigator.clipboard.writeText(calendarLinkData.url)
      toast({
        title: 'Calendar URL copied',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const hasCalendarLink = !!calendarLinkData?.url

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      padding="24px"
    >
      <Heading size="small" mb={3}>
        Sync Blueprint to other calendars
      </Heading>

      {hasCalendarLink ? (
        <>
          <Text mb={3}>
            Use this .ics link to add Blueprint to an external calendar of your
            choosing.
          </Text>
          <HStack spacing={3} mb={6}>
            <Box
              bg="#f9f9f9"
              border="1px solid"
              borderColor="rgba(55, 60, 65, 0.14)"
              borderRadius="8px"
              px={6}
              py={3}
              minHeight="48px"
              display="flex"
              alignItems="center"
              minWidth={0}
              maxWidth="100%"
            >
              <HStack spacing={2} flex={1} minWidth={0}>
                <LinkIcon fill="#AAAAAA" size="md" flexShrink={0} />
                <Text
                  fontSize="16px"
                  color="#2d54e8"
                  lineHeight="22px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  minWidth={0}
                >
                  {calendarLinkData?.url}
                </Text>
              </HStack>
            </Box>
            <IconButton
              aria-label="Copy calendar link"
              icon={<CopyIcon />}
              onClick={handleCopyLink}
              size="lg"
              fill="#3E3E3E"
              borderColor="rgba(55, 60, 65, 0.14)"
              variant="outline"
              minWidth="48px"
              height="48px"
              borderRadius="8px"
            />
          </HStack>
        </>
      ) : (
        <>
          <Text mb={3}>
            Publish your Blueprint calendar to use it with other apps.
          </Text>
          <Button
            size="lg"
            mb={6}
            colorScheme="blue"
            borderRadius="8px"
            onClick={handlePublishCalendar}
            isLoading={isCreating}
            loadingText="Publishing..."
          >
            Publish calendar
          </Button>
        </>
      )}

      <Divider mb={6} />

      <HStack align="flex-start" spacing={8} mb={10} ml={3}>
        <Box flex={1} maxWidth="320px">
          <Text fontSize="xs" fontWeight="bold" mb={3} color="black">
            Note:
          </Text>
          <Text fontSize="md">
            For privacy purposes client names will appear on your external
            calendar by their initials only
          </Text>
        </Box>

        <Box flex={1} maxWidth="350px">
          <Text fontSize="xs" fontWeight="regular" mb={3} color="black">
            Example:
          </Text>
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="8px"
            overflow="hidden"
            minHeight="123px"
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* Calendar grid lines */}
            <Box position="absolute" inset={0}>
              {[0, 1, 2, 3].map(i => (
                <Box
                  key={i}
                  position="absolute"
                  top={`${i * 25}%`}
                  left={0}
                  right={0}
                  height="1px"
                  bg="gray.100"
                />
              ))}
            </Box>

            {/* Calendar Example */}
            <Box
              position="relative"
              height="86px"
              bg="#fffbf0"
              borderRadius="4px"
              border="1px solid"
              borderColor="#ffde84"
              borderLeftWidth="4px"
              borderLeftColor="#ffde84"
              p={2}
              maxWidth="180px"
              minWidth="180px"
              zIndex={1}
            >
              <VStack align="stretch" spacing={1}>
                <HStack spacing={1}>
                  <Text
                    fontSize="14px"
                    fontWeight="bold"
                    color="#c24d15"
                    lineHeight="19px"
                  >
                    J.A. Session
                  </Text>
                </HStack>
                <Text fontSize="14px" color="#c24d15" lineHeight="19px">
                  9:30 am - 10 am
                </Text>
              </VStack>
            </Box>
          </Box>
        </Box>
      </HStack>

      <Box
        bg="#f6f7fe"
        border="1px solid"
        borderColor="gray.100"
        borderRadius="8px"
        p={3}
      >
        <HStack align="flex-start" spacing={3}>
          <Box
            width="16px"
            height="16px"
            flexShrink={0}
            mt="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <InfoFilledIcon fill="#2d54e8" size="sm" />
          </Box>
          <Text fontSize="xs" color="gray.700" lineHeight="16px">
            Once synced, your Blueprint calendar will publish
            appointments—including telehealth links—to your external calendar.
            {'\n'}
            By enabling this, you acknowledge your responsibility to follow
            HIPAA guidelines and safeguard all calendar information.
          </Text>
        </HStack>
      </Box>
    </Box>
  )
}

export default SyncBlueprintCalendar
